import type { BasicUser, MessageTemplateSubjectVariant, OrganizationRole } from '@st/sdk'
import type { SdkFailure } from '~/effects'
import { extname } from '@st/util/file'
import { Effect } from 'effect'
import {
  BrowserResources,
  FileStorage,
  IdGenerator,
  SdkClient,
  type BrowserResourceRef
} from '~/effects'
import { runSdkRequest } from '~/features/module-runtime'

export type EditOrganizationAttributes = {
  name?: string
  email?: string
  logo?: string
  appsInStack?: string[]
}

export type OrganizationWriteOutcome =
  | { type: 'organizationWriteFailed' }
  | {
      type: 'organizationUpdated'
      modifications: EditOrganizationAttributes
    }
  | {
      type: 'teamMemberAdded'
      user: BasicUser
      role: OrganizationRole
    }
  | { type: 'teamInviteResent'; email: string }
  | { type: 'teamMemberRemoved'; userId: string; email: string }
  | {
      type: 'messageTemplatesUpdated'
      messageTemplateIds: string[]
      comment: string
      subjectVariant: MessageTemplateSubjectVariant
    }
  | { type: 'folderTagCreated' }
  | {
      type: 'folderTagEdited'
      tagId: string
      label: string
      color: string
    }
  | { type: 'folderTagDeleted'; tagId: string }
  | { type: 'organizationLogoUploaded'; filepath: string }
  | { type: 'organizationLogoUploadFailed' }

const organizationWriteFailed = { type: 'organizationWriteFailed' } as const

export function saveMessageTemplates(args: {
  organizationId: string
  messageTemplateIds: string[]
  comment: string
  subjectVariant: MessageTemplateSubjectVariant
}) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* recoverTransientOrganizationRequest(
      sdk.send({
        type: 'organizations/editMessageTemplates',
        organizationId: args.organizationId,
        messageTemplateIds: args.messageTemplateIds,
        messageTemplateComment: args.comment,
        messageTemplateSubjectVariant: args.subjectVariant
      })
    )
    if (!result.ok) return organizationWriteFailed

    return {
      type: 'messageTemplatesUpdated',
      messageTemplateIds: args.messageTemplateIds,
      comment: args.comment,
      subjectVariant: args.subjectVariant
    } as const
  })
}

export function createFolderTag(args: { organizationId: string; label: string; color: string }) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* recoverTransientOrganizationRequest(
      sdk.send({
        type: 'organizations/createFolderTag',
        organizationId: args.organizationId,
        label: args.label,
        color: args.color
      })
    )
    if (!result.ok) return organizationWriteFailed

    // The endpoint does not return the generated tag ID, so the state machine
    // requests one background refresh after the write succeeds.
    return { type: 'folderTagCreated' } as const
  })
}

export function editFolderTag(args: {
  organizationId: string
  tagId: string
  label: string
  color: string
}) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* recoverTransientOrganizationRequest(
      sdk.send({
        type: 'organizations/editFolderTag',
        organizationId: args.organizationId,
        tagId: args.tagId,
        label: args.label,
        color: args.color
      })
    )
    if (!result.ok) return organizationWriteFailed

    return {
      type: 'folderTagEdited',
      tagId: args.tagId,
      label: args.label,
      color: args.color
    } as const
  })
}

export function deleteFolderTag(args: { organizationId: string; tagId: string }) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* recoverTransientOrganizationRequest(
      sdk.send({
        type: 'organizations/deleteFolderTag',
        organizationId: args.organizationId,
        tagId: args.tagId
      })
    )
    if (!result.ok) return organizationWriteFailed

    return {
      type: 'folderTagDeleted',
      tagId: args.tagId
    } as const
  })
}

export function inviteMemberByEmail(args: {
  organizationId: string
  email: string
  role: OrganizationRole
}) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const userResult = yield* recoverTransientOrganizationRequest(
      sdk.send({ type: 'accounts/getOrCreateUserByEmail', email: args.email })
    )
    if (!userResult.ok) return organizationWriteFailed

    const user = userResult.response.user
    const addResult = yield* recoverTransientOrganizationRequest(
      sdk.send({
        type: 'organizations/addMemberToOrganization',
        organizationId: args.organizationId,
        userId: user.id,
        role: args.role
      })
    )
    if (!addResult.ok) return organizationWriteFailed

    const inviteResult = yield* recoverTransientOrganizationRequest(
      sdk.send({
        type: 'organizations/sendTeamInviteEmail',
        organizationId: args.organizationId,
        userId: user.id
      })
    )
    if (!inviteResult.ok) return organizationWriteFailed

    return {
      type: 'teamMemberAdded',
      user,
      role: args.role
    } as const
  })
}

export function resendTeamInvite(args: { organizationId: string; userId: string; email: string }) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* recoverTransientOrganizationRequest(
      sdk.send({
        type: 'organizations/sendTeamInviteEmail',
        organizationId: args.organizationId,
        userId: args.userId
      })
    )
    if (!result.ok) return organizationWriteFailed

    return { type: 'teamInviteResent', email: args.email } as const
  })
}

export function removeOrganizationMember(args: {
  organizationId: string
  userId: string
  email: string
}) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* recoverTransientOrganizationRequest(
      sdk.send({
        type: 'organizations/removeMemberFromOrganization',
        organizationId: args.organizationId,
        userId: args.userId
      })
    )
    if (!result.ok) return organizationWriteFailed

    return {
      type: 'teamMemberRemoved',
      userId: args.userId,
      email: args.email
    } as const
  })
}

export function saveOrganization(args: {
  organizationId: string
  modifications: EditOrganizationAttributes
}) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* recoverTransientOrganizationRequest(
      sdk.send({
        type: 'organizations/updateOrganization',
        organizationId: args.organizationId,
        name: args.modifications.name,
        email: args.modifications.email,
        logo: args.modifications.logo,
        appsInStack: args.modifications.appsInStack
      })
    )
    if (!result.ok) return organizationWriteFailed

    return {
      type: 'organizationUpdated',
      modifications: args.modifications
    } as const
  })
}

export function uploadOrganizationLogo(args: {
  organizationId: string
  file: BrowserResourceRef<File>
}) {
  return Effect.gen(function* () {
    const browserResources = yield* BrowserResources
    const file = yield* browserResources.take(args.file)
    const fileStorage = yield* FileStorage
    const idGenerator = yield* IdGenerator
    const logoId = yield* idGenerator.generate
    const destinationPath = `organizations/${args.organizationId}/public/${logoId}${extname(
      file.name
    )}`

    return yield* fileStorage.upload(file, { destinationPath }).pipe(
      Effect.as({
        type: 'organizationLogoUploaded',
        filepath: destinationPath
      } as const),
      Effect.catchTag('FileStorageUploadTransientError', () =>
        Effect.succeed({ type: 'organizationLogoUploadFailed' } as const)
      )
    )
  })
}

function recoverTransientOrganizationRequest<Response, Env>(
  request: Effect.Effect<Response, SdkFailure, Env>
) {
  return runSdkRequest(request)
}
